import { LegalPage } from './LegalPage';
import { LEGAL_CONFIG, LEGAL_PATHS } from './config';

const { entity, product, domain, supportEmail, governingLawState } = LEGAL_CONFIG;

/**
 * Terms of Service.
 *
 * Mirrors how the product actually works: a prepaid balance topped up through
 * Stripe and drawn down per AI operation, user-owned content processed by
 * third-party AI providers, and publishing to social accounts the user
 * connects by OAuth.
 */
export function TermsOfService() {
    return (
        <LegalPage title="Terms of Service">
            <p>
                These terms are an agreement between you and {entity} (&ldquo;we&rdquo;,
                &ldquo;us&rdquo;) covering your use of {product} at {domain} (the
                &ldquo;Service&rdquo;). By creating an account or using the Service, you accept them.
                If you do not agree, do not use the Service.
            </p>

            <h2>1. Eligibility and accounts</h2>
            <p>
                You must be at least 18 years old, or the age of majority where you live, and legally
                able to enter a contract. If you use the Service for an organization, you confirm you
                are authorized to bind that organization, and &ldquo;you&rdquo; means both you and it.
            </p>
            <p>
                You are responsible for everything that happens under your account and for keeping
                your sign-in credentials secure. Tell us promptly at{' '}
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a> if you suspect unauthorized use.
                One person or organization per account; do not share credentials.
            </p>

            <h2>2. The Service</h2>
            <p>
                {product} lets you upload media, generate and edit video with AI assistance, publish
                the result to social accounts you connect, and review performance reporting. We may
                add, change, or remove features. We aim for continuous availability but do not
                guarantee it, and we may suspend the Service for maintenance, security, or capacity
                reasons.
            </p>

            <h2>3. Your content</h2>
            <p>
                <strong>You keep ownership of everything you upload and everything you create with
                the Service.</strong> We claim no ownership of your media, projects, or output.
            </p>
            <p>
                You grant us a worldwide, non-exclusive, royalty-free license to host, store, copy,
                transmit, transcode, modify, display, and process your content strictly to operate the
                Service for you &mdash; including sending it to the cloud and AI providers described
                in our <a href={LEGAL_PATHS.privacy}>Privacy Policy</a>, and publishing it to accounts
                you direct us to publish to. This license exists only to run the Service and ends when
                you delete the content or your account, subject to routine backup cycles.
            </p>
            <p>You represent that, for all content you upload or publish through the Service:</p>
            <ul>
                <li>You own it or have all rights and permissions needed to use it this way.</li>
                <li>
                    You have cleared any music, footage, images, fonts, trademarks, and likenesses it
                    contains, and obtained any consent or release required from people who appear in
                    it.
                </li>
                <li>It does not infringe anyone&apos;s rights or break any law.</li>
            </ul>

            <h2>4. AI-generated output</h2>
            <div className="legal-callout">
                <p>
                    AI systems make mistakes. Output may be inaccurate, misleading, derivative of
                    existing works, or unsuitable for your purpose, and similar output may be
                    generated for other users. <strong>You are responsible for reviewing output before
                    you publish or rely on it</strong>, and for making sure what you publish complies
                    with the law and with the rules of the platform you publish to.
                </p>
            </div>
            <p>
                As between you and us, we assign you whatever rights we may have in the output
                generated for you. We make no representation that output is original or that you can
                obtain intellectual-property protection in it.
            </p>

            <h2>5. Acceptable use</h2>
            <p>You may not use the Service to create, process, or publish content that:</p>
            <ul>
                <li>Infringes copyright, trademark, privacy, publicity, or other rights.</li>
                <li>Is unlawful, defamatory, harassing, hateful, or incites violence.</li>
                <li>Sexually exploits or endangers minors, or is non-consensual sexual content.</li>
                <li>
                    Impersonates a real person or organization, or is a synthetic depiction of a real
                    person presented as authentic without clear disclosure and, where required,
                    consent.
                </li>
                <li>Is deceptive, fraudulent, spam, or coordinated inauthentic activity.</li>
                <li>Contains malware or is intended to compromise systems or accounts.</li>
            </ul>
            <p>You also may not:</p>
            <ul>
                <li>
                    Reverse engineer, scrape, or attempt to extract the models, prompts, or source
                    behind the Service, or use it to build a competing product.
                </li>
                <li>
                    Circumvent usage limits, metering, rate limits, or access controls, or share
                    access with people who are not authorized users of your account.
                </li>
                <li>
                    Violate the terms of any platform you connect, or use the Service in a way that
                    would cause us to violate them.
                </li>
                <li>Probe or disrupt the integrity or performance of the Service.</li>
            </ul>
            <p>
                We may remove content or suspend accounts that violate this section. Where we can, we
                will tell you why and give you a chance to respond first, but we may act immediately
                where there is legal risk, harm to others, or a threat to the Service.
            </p>

            <h2>6. Connected platforms</h2>
            <p>
                When you connect a YouTube, TikTok, Facebook, or Instagram account, you authorize us
                to act on that account as you direct &mdash; publishing content you approve and
                retrieving performance data. Your use of each platform remains governed by that
                platform&apos;s own terms, including the{' '}
                <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">
                    YouTube Terms of Service
                </a>
                . We are not responsible for a platform&apos;s decisions about your content or
                account, for changes to its API, or for content that fails to publish as a result. You
                can disconnect any account at any time from within the Service.
            </p>

            <h2>7. Fees and billing</h2>
            <p>
                The Service is prepaid. You add funds to your account balance through Stripe, and AI
                features draw down that balance as you use them. There is no subscription and no
                recurring fee. If your balance will not cover an operation, the Service declines it
                and asks you to add funds &mdash; <strong>we never charge you more than the balance
                you have added.</strong>
            </p>
            <p>
                Larger top-ups carry bonus balance: the amount credited to your account can exceed
                the amount charged (for example, a $500 top-up credits $550). Bonus balance is
                promotional credit, spendable exactly like the rest of your balance but not itself
                money you paid; a refund returns what you actually paid, and removes the credit the
                payment granted.
            </p>
            <p>
                Before an AI operation runs we place a hold on your balance for its estimated cost,
                and when it finishes we deduct what it actually cost and release the rest. Estimates
                are deliberately conservative, so a hold is usually larger than the final charge. If
                an operation fails, you are not charged for it and the entire hold is released.
                Costlier operations show you an estimate and ask you to confirm before they start.
                Rates for each feature are shown in the Service, and your balance, holds, and
                per-operation history are visible in your account at any time.
            </p>
            <p>
                <strong>Your balance does not expire and is refundable.</strong> You may request a
                refund of any unspent balance at{' '}
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a> and we will return it to your
                original payment method. Funds already spent on completed operations are not
                refundable, because the underlying processing has been paid for on your behalf.
            </p>
            <p>
                Prices may change; we will give notice before a change applies to future usage, and a
                change never affects the value of funds you have already added. Charges are exclusive
                of taxes, and you are responsible for any taxes other than those on our income.
                Dispute a charge you believe is wrong by contacting{' '}
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a> within 60 days of it appearing.
            </p>
            <p>
                If a payment is charged back through your bank, the balance it granted is removed
                from your account and new top-ups are paused while we look into it. Contact{' '}
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a> and we will resolve it — this
                is a pause, not a closure, and if the chargeback is resolved in our favour the
                balance is returned to your account automatically. Contacting us first is always
                faster than a chargeback, and unspent balance is refundable on request anyway.
            </p>

            <h2>8. Storage, limits, and retention</h2>
            <p>
                Accounts are subject to workspace counts and storage limits shown in the Service. We
                may enforce or adjust these limits to keep the Service stable.
            </p>
            <div className="legal-callout">
                <p>
                    <strong>We do not store your projects indefinitely.</strong> A project,
                    workspace, or the media stored in it may be deleted after a period of inactivity
                    &mdash; at least 180 days, with the period currently in force shown in the
                    Service. Before we delete anything on this basis we will email the address on
                    your account at least 30 days beforehand and tell you what is scheduled for
                    deletion. Opening or editing a project during that window makes it active again
                    and cancels the deletion. Deletion is permanent, so export anything you want to
                    keep.
                </p>
            </div>
            <p>
                Files that exist only as an intermediate step toward a finished result &mdash; for
                example the individual generated clips that have already been assembled into an
                output video &mdash; may be removed on a shorter schedule once the finished result
                is stored.
            </p>
            <p>
                If your account is closed or suspended for non-payment, we may delete stored content
                after giving you notice and a reasonable opportunity to export it.
            </p>

            <h2>9. Our intellectual property</h2>
            <p>
                The Service, its software, interface, and the {product} name and marks belong to us
                and our licensors. Nothing here transfers those rights to you. You get a limited,
                revocable, non-transferable right to use the Service under these terms. Feedback you
                send us may be used without restriction or obligation.
            </p>

            <h2>10. Copyright complaints</h2>
            <p>
                If you believe content processed through the Service infringes your copyright, send a
                notice to <a href={`mailto:${supportEmail}`}>{supportEmail}</a> with the material at
                issue, your contact details, a statement of good-faith belief, and a statement under
                penalty of perjury that you are authorized to act. We remove infringing material and
                terminate repeat infringers.
            </p>

            <h2>11. Termination</h2>
            <p>
                You may stop using the Service and close your account at any time; see the{' '}
                <a href={LEGAL_PATHS.privacy}>Privacy Policy</a> for how deletion works. We may
                suspend or terminate your access for breach of these terms, non-payment, legal
                requirement, or prolonged inactivity. On termination, your right to use the Service
                ends and we delete your content per the Privacy Policy. Sections that by their nature
                should survive &mdash; fees incurred, content representations, IP, disclaimers,
                liability, indemnity, and governing law &mdash; survive termination.
            </p>

            <h2>12. Disclaimers</h2>
            <p>
                THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo;, WITHOUT
                WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A
                PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE
                WILL BE UNINTERRUPTED OR ERROR-FREE, THAT CONTENT WILL BE PRESERVED WITHOUT LOSS, OR
                THAT AI OUTPUT WILL BE ACCURATE, ORIGINAL, OR FIT FOR YOUR PURPOSE. Keep your own
                copies of anything you cannot afford to lose.
            </p>

            <h2>13. Limitation of liability</h2>
            <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY IS LIABLE FOR INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, OR FOR LOST PROFITS,
                REVENUE, DATA, OR GOODWILL, EVEN IF ADVISED OF THE POSSIBILITY. OUR TOTAL LIABILITY
                ARISING OUT OF OR RELATING TO THE SERVICE IS LIMITED TO THE GREATER OF THE AMOUNTS YOU
                PAID US IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR ONE HUNDRED US
                DOLLARS ($100). Some jurisdictions do not allow these limits, in which case they apply
                to the fullest extent permitted.
            </p>

            <h2>14. Indemnity</h2>
            <p>
                You will defend, indemnify, and hold harmless {entity} and its officers, employees,
                and agents from claims, damages, and reasonable legal fees arising from your content,
                your use of the Service, or your breach of these terms or of any law or third-party
                right.
            </p>

            <h2>15. Governing law and disputes</h2>
            <p>
                These terms are governed by the laws of the State of {governingLawState}, without
                regard to its conflict-of-laws rules. You and we agree to the exclusive jurisdiction
                of the state and federal courts located in {governingLawState} for any dispute not
                subject to small-claims court. Before filing, contact us at{' '}
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a> and give us 30 days to resolve
                it informally.
            </p>

            <h2>16. Changes to these terms</h2>
            <p>
                We may update these terms. We revise the effective date above and, for material
                changes, give notice in the Service or by email before they take effect and ask you to
                accept the new version. Continued use after a change takes effect means you accept it.
            </p>

            <h2>17. General</h2>
            <p>
                These terms and the <a href={LEGAL_PATHS.privacy}>Privacy Policy</a> are the entire
                agreement between us about the Service. If a provision is unenforceable, the rest
                stays in force. Our failure to enforce a provision is not a waiver. You may not assign
                these terms without our consent; we may assign them in a merger, acquisition, or sale
                of assets. Nothing here creates a partnership, agency, or employment relationship.
            </p>

            <h2>18. Contact</h2>
            <p>
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                {LEGAL_CONFIG.mailingAddress ? (
                    <>
                        <br />
                        {entity}, {LEGAL_CONFIG.mailingAddress}
                    </>
                ) : null}
            </p>
        </LegalPage>
    );
}

export default TermsOfService;
